import { useEffect, useState } from "react";
import { Check, Pencil, Plus, X } from "lucide-react";
import { FormattedMessage, useIntl } from "react-intl";

import type { EditableBrandFields, ValueProp } from "./confirm-review-data";
import { buildValuePropFields, formatValuePropText } from "./confirm-review-data";
import { ConfirmReviewCard } from "./confirm-review-card";

const createEmptyValueProp = (): ValueProp => ({
  id: `core-promise-${Date.now().toString(36)}`,
  title: "",
  description: "",
  text: "",
});

export function ValuePropsStep({
  valueProps,
  onSave,
}: {
  valueProps: ValueProp[];
  onSave: (fields: EditableBrandFields) => Promise<void> | void;
}) {
  const intl = useIntl();
  const [items, setItems] = useState(valueProps);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftTitle, setDraftTitle] = useState("");
  const [newItemId, setNewItemId] = useState<string | null>(null);

  useEffect(() => {
    setItems(valueProps);
    setNewItemId(null);
  }, [valueProps]);

  const persist = async (next: ValueProp[]) => {
    setItems(next);
    await onSave(buildValuePropFields(next));
  };

  const startEditing = (item: ValueProp) => {
    setEditingId(item.id);
    setDraftTitle(item.title);
  };

  const cancelEditing = () => {
    if (editingId && editingId === newItemId) {
      setItems((current) => current.filter((item) => item.id !== editingId));
      setNewItemId(null);
    }

    setEditingId(null);
    setDraftTitle("");
  };

  const saveEditing = async (id: string) => {
    const title = draftTitle.trim();
    const currentItem = items.find((item) => item.id === id);
    const description = currentItem?.description ?? "";
    const text = formatValuePropText({ title, description });
    if (!text) {
      cancelEditing();
      return;
    }

    await persist(
      items.map((item) => (item.id === id ? { ...item, title, description, text } : item)),
    );
    if (newItemId === id) {
      setNewItemId(null);
    }
    setEditingId(null);
    setDraftTitle("");
  };

  const removeItem = async (id: string) => {
    const next = items.filter((item) => item.id !== id);
    if (editingId === id) {
      cancelEditing();
    }
    if (newItemId === id) {
      setNewItemId(null);
    }
    await persist(next);
  };

  const addItem = () => {
    if (newItemId) {
      setEditingId(newItemId);
      setDraftTitle("");
      return;
    }

    const item = createEmptyValueProp();
    setItems((current) => [...current, item]);
    setEditingId(item.id);
    setNewItemId(item.id);
    setDraftTitle("");
  };

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-3">
      <ConfirmReviewCard
        label={
          <FormattedMessage
            id="brandOnboarding.confirm.whatYouDo"
            defaultMessage="Key differentiators"
          />
        }
      >
        <div className="divide-y divide-border/45">
          {items.map((item, index) => (
            <div key={item.id} data-testid={`usp-row-${index}`}>
              {editingId === item.id ? (
                <div className="space-y-3 py-5 pr-1">
                  <input
                    value={draftTitle}
                    onChange={(event) => setDraftTitle(event.target.value)}
                    autoFocus
                    data-testid={`input-usp-title-${index}`}
                    aria-label={intl.formatMessage({
                      id: "brandOnboarding.confirm.valuePropTitlePlaceholder",
                      defaultMessage: "Key differentiator",
                    })}
                    placeholder={intl.formatMessage({
                      id: "brandOnboarding.confirm.valuePropTitlePlaceholder",
                      defaultMessage: "Key differentiator",
                    })}
                    className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm font-semibold text-foreground outline-none ring-primary/30 placeholder:text-muted-foreground focus:ring-2"
                  />
                  <div className="flex flex-wrap justify-end gap-2">
                    <button
                      type="button"
                      className="inline-flex h-8 items-center gap-1.5 rounded-full px-3 text-xs font-semibold text-muted-foreground transition hover:bg-muted hover:text-foreground"
                      onClick={cancelEditing}
                    >
                      <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
                    </button>
                    <button
                      type="button"
                      className="inline-flex h-8 items-center gap-1.5 rounded-full bg-foreground px-3 text-xs font-semibold text-background transition hover:bg-foreground/88 disabled:cursor-not-allowed disabled:opacity-45"
                      onClick={() => void saveEditing(item.id)}
                      disabled={!draftTitle.trim()}
                    >
                      <Check className="h-3.5 w-3.5" aria-hidden />
                      <FormattedMessage id="common.save" defaultMessage="Save" />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="group grid grid-cols-[14px_1fr_auto] items-start gap-3 py-5 first:pt-1 last:pb-1">
                  <span
                    aria-hidden
                    className="mt-3 h-1.5 w-1.5 justify-self-center rounded-full bg-[#C8FF00] shadow-[0_0_0_4px_rgba(13,31,45,0.04)]"
                  />
                  <div className="min-w-0 space-y-1">
                    <p className="text-base font-semibold tracking-tight text-foreground">
                      {item.title ||
                        intl.formatMessage({
                          id: "brandOnboarding.confirm.valuePropTitlePlaceholder",
                          defaultMessage: "Key differentiator",
                        })}
                    </p>
                    {item.description ? (
                      <p className="text-sm leading-6 text-muted-foreground">{item.description}</p>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => startEditing(item)}
                      data-testid={`button-usp-edit-${index}`}
                      aria-label={intl.formatMessage({ id: "common.edit", defaultMessage: "Edit" })}
                      className="flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground transition hover:bg-muted hover:text-foreground"
                    >
                      <Pencil className="h-3.5 w-3.5" aria-hidden />
                    </button>
                    <button
                      type="button"
                      onClick={() => void removeItem(item.id)}
                      data-testid={`button-usp-remove-${index}`}
                      aria-label={intl.formatMessage({
                        id: "brandOnboarding.confirm.removeValueProp",
                        defaultMessage: "Remove differentiator",
                      })}
                      className="flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground transition hover:bg-destructive/10 hover:text-destructive"
                    >
                      <X className="h-3.5 w-3.5" aria-hidden />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        <button
          type="button"
          className="mt-3 inline-flex w-fit items-center gap-2 rounded-md text-sm font-medium text-muted-foreground transition-[color,font-weight] hover:font-semibold hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
          onClick={addItem}
          data-testid="button-usp-add"
        >
          <Plus className="h-4 w-4" aria-hidden />
          <FormattedMessage
            id="brandOnboarding.confirm.addValueProp"
            defaultMessage="Add differentiator"
          />
        </button>
      </ConfirmReviewCard>
    </div>
  );
}
